/**
 * Phase 4c — Candidate AI Chat slide-out panel.
 *
 * Strict UI discipline (matches the legal spine in chat_llm.CHAT_SYSTEM_PROMPT):
 *   - All copy is AUDIT/SEARCH language: "Ask what was said in these interviews",
 *     "Find a quote", "Search the audit record". NEVER insights/evaluation
 *     language like "AI candidate insights" or "Evaluate candidate".
 *   - Admin sessions are visible to ALL admins in the workspace — shown with
 *     an explicit "Admin view" badge so the audit seat is genuinely complete.
 *   - Running session cost displayed inline; per-workspace daily budget
 *     remainder pinned to the footer.
 *   - Compliance refusals are styled distinctly so recruiters learn what
 *     questions are out of scope.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquareText, Send, X, Share2, Archive, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { toast } from "sonner";
import ChatAnswer from "./ChatAnswer";

const STROKE = 1.5;

function fmtCost(usd) {
  if (usd == null) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function CompliantBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
      data-testid="chat-refusal-badge"
      title="Out-of-scope refusal — character / tone / fit judgments are intentionally declined for legal compliance (EEOC, EU AI Act, NYC LL144)."
    >
      <ShieldCheck className="h-2.5 w-2.5" strokeWidth={STROKE} />
      Out of scope
    </span>
  );
}

export default function CandidateChatPanel({ candidateId, open, onClose, initialDraft }) {
  const { member } = useAuth();
  const isAdmin = member?.role === "admin";

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);    // detail incl. messages
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [quota, setQuota] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const scrollerRef = useRef(null);

  // Pre-fill draft on open whenever initialDraft is provided (e.g. when the
  // pill is opened from an interview-detail page — "Tell me about Round N").
  // Only fires when the panel opens, not on every render.
  useEffect(() => {
    if (open && initialDraft) setDraft(initialDraft);
  }, [open, initialDraft]);

  // Load sessions list + quota whenever the panel opens
  useEffect(() => {
    if (!open || !candidateId) return;
    setLoadingList(true);
    Promise.all([
      api.get(`/candidates/${candidateId}/chat/sessions`),
      api.get(`/chat/quota`),
    ])
      .then(([s, q]) => {
        setSessions(s.data || []);
        setQuota(q.data);
        // Auto-select most recent non-archived session
        const recent = (s.data || []).find((x) => !x.archived);
        if (recent) setActiveSessionId(recent.session_id);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load chat"))
      .finally(() => setLoadingList(false));
  }, [open, candidateId]);

  // Load active session detail (messages) whenever activeSessionId changes
  useEffect(() => {
    if (!activeSessionId || !candidateId) {
      setActiveSession(null);
      return;
    }
    api
      .get(`/candidates/${candidateId}/chat/sessions/${activeSessionId}`)
      .then(({ data }) => setActiveSession(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to open session"));
  }, [activeSessionId, candidateId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [activeSession?.messages?.length]);

  async function startNewSession() {
    setCreating(true);
    try {
      const { data } = await api.post(`/candidates/${candidateId}/chat/sessions`, {});
      setSessions((prev) => [data, ...prev]);
      setActiveSessionId(data.session_id);
      setDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start chat");
    } finally {
      setCreating(false);
    }
  }

  async function send() {
    const content = draft.trim();
    if (!content || sending || !activeSessionId) return;
    setSending(true);
    // Optimistic insert of the user message so the UI is responsive
    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      session_id: activeSessionId,
      role: "user",
      content,
      tokens_in: 0, tokens_out: 0, est_cost_usd: 0,
      refusal_triggered: false,
      created_at: new Date().toISOString(),
    };
    setActiveSession((s) =>
      s ? { ...s, messages: [...(s.messages || []), optimistic] } : s,
    );
    setDraft("");
    try {
      const { data: assistantMsg } = await api.post(
        `/candidates/${candidateId}/chat/sessions/${activeSessionId}/messages`,
        { content },
      );
      // Refresh full detail so totals + ordering are authoritative
      const detail = await api.get(
        `/candidates/${candidateId}/chat/sessions/${activeSessionId}`,
      );
      setActiveSession(detail.data);
      // Refresh quota (workspace-wide spend may have increased)
      const q = await api.get(`/chat/quota`);
      setQuota(q.data);
      // Update sessions list totals
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === activeSessionId
            ? {
                ...s,
                message_count: detail.data.message_count,
                total_cost_usd: detail.data.total_cost_usd,
                updated_at: detail.data.updated_at,
              }
            : s,
        ),
      );
      // Subtle toast if compliance refusal
      if (assistantMsg?.refusal_triggered) {
        toast("That kind of question is out of scope for an audit record.", {
          description: "Ask about what was said instead.",
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to send";
      toast.error(detail);
      // Roll back optimistic message
      setActiveSession((s) =>
        s
          ? {
              ...s,
              messages: (s.messages || []).filter(
                (m) => m.message_id !== optimistic.message_id,
              ),
            }
          : s,
      );
    } finally {
      setSending(false);
    }
  }

  async function toggleShare() {
    if (!activeSession) return;
    try {
      const { data } = await api.patch(
        `/candidates/${candidateId}/chat/sessions/${activeSessionId}/share`,
        { shared_with_team: !activeSession.shared_with_team },
      );
      setActiveSession((s) => ({ ...s, shared_with_team: data.shared_with_team }));
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === activeSessionId
            ? { ...s, shared_with_team: data.shared_with_team }
            : s,
        ),
      );
      toast.success(
        data.shared_with_team
          ? "Shared with team. Admins always see all sessions."
          : "Made private. Admins still see all sessions.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update sharing");
    }
  }

  async function archiveSession() {
    if (!activeSession) return;
    if (!confirm("Archive this chat? It'll be hidden but still visible to admins for audit.")) return;
    try {
      await api.delete(`/candidates/${candidateId}/chat/sessions/${activeSessionId}`);
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === activeSessionId ? { ...s, archived: true } : s,
        ),
      );
      setActiveSessionId(null);
      setActiveSession(null);
      toast.success("Chat archived");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to archive");
    }
  }

  const turnsUsed = (activeSession?.messages || []).filter((m) => m.role === "user").length;
  const turnsRemaining = (quota?.max_turns_per_session || 20) - turnsUsed;
  const sessionMine = activeSession?.created_by === member?.user_id;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-ink-900/20 backdrop-blur-sm"
      onClick={onClose}
      data-testid="chat-panel-overlay"
    >
      <div
        className="flex h-full w-full max-w-xl flex-col border-l border-ink-200 bg-ink-50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="chat-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ink-200 bg-white px-5 py-4">
          <div>
            <div className="font-display text-[18px] font-medium text-ink-900" data-testid="chat-panel-title">
              Ask what was said
            </div>
            <div className="mt-0.5 text-[12px] text-ink-500">
              Search the audit record across all interview rounds — answers are quoted verbatim from the transcripts.
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            data-testid="chat-close"
            aria-label="Close chat panel"
          >
            <X className="h-4 w-4" strokeWidth={STROKE} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Sessions list — compact, horizontal */}
          <div className="border-b border-ink-200 bg-white px-5 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
                {isAdmin ? "All chats (admin view)" : "Your chats + team-shared"}
              </div>
              <button
                onClick={startNewSession}
                disabled={creating}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-800 bg-emerald-800 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
                data-testid="chat-new-session"
              >
                {creating ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={STROKE} /> : <MessageSquareText className="h-3 w-3" strokeWidth={STROKE} />}
                New chat
              </button>
            </div>
            {loadingList && (
              <div className="mt-2 text-[12px] text-ink-500">Loading chats…</div>
            )}
            {!loadingList && sessions.length === 0 && (
              <div className="mt-2 text-[12px] text-ink-500" data-testid="chat-no-sessions">
                No chats yet. Start one to ask what was said in this candidate's interviews.
              </div>
            )}
            {sessions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {sessions.map((s) => {
                  const isActive = s.session_id === activeSessionId;
                  return (
                    <button
                      key={s.session_id}
                      onClick={() => setActiveSessionId(s.session_id)}
                      data-testid={`chat-session-${s.session_id}`}
                      className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition ${
                        isActive
                          ? "border-emerald-800 bg-emerald-50 text-emerald-900"
                          : "border-ink-200 bg-white text-ink-700 hover:border-ink-300"
                      } ${s.archived ? "opacity-50" : ""}`}
                    >
                      {s.title || `Chat ${new Date(s.created_at).toLocaleDateString()}`}
                      <span className="text-ink-400">·</span>
                      <span className="text-ink-500">{s.message_count} msg</span>
                      <span className="text-ink-400">·</span>
                      <span className="text-ink-500">{fmtCost(s.total_cost_usd)}</span>
                      {s.shared_with_team && (
                        <Share2 className="h-2.5 w-2.5 text-ink-500" strokeWidth={STROKE} />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Active session — messages + composer */}
          {!activeSession && !loadingList && (
            <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="chat-empty-hint">
              <MessageSquareText className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
              <p className="mt-3 text-[14px] text-ink-600">
                Start a new chat to search this candidate's audit record.
              </p>
              <p className="mt-1 text-[12px] text-ink-500 max-w-sm">
                You can ask things like: <em>"What did they say about their last role?"</em>, <em>"Did they mention managing budgets?"</em>, <em>"Find quotes about leadership."</em>
              </p>
              <p className="mt-3 text-[11px] text-ink-400 max-w-sm">
                Questions about tone, confidence, fit or character are intentionally out of scope for legal compliance.
              </p>
            </div>
          )}

          {activeSession && (
            <>
              {/* Session header strip */}
              <div className="flex items-center justify-between border-b border-ink-200 bg-ink-50 px-5 py-2">
                <div className="text-[11px] text-ink-600">
                  Loaded {activeSession.included_interview_ids?.length || 0} round{(activeSession.included_interview_ids?.length || 0) === 1 ? "" : "s"} · {turnsUsed}/{quota?.max_turns_per_session || 20} turns · {fmtCost(activeSession.total_cost_usd)}
                  {(activeSession.excluded_interview_ids?.length || 0) > 0 && (
                    <span className="ml-2 text-amber-700" title="Some rounds were excluded due to total length">
                      ⚠ {activeSession.excluded_interview_ids.length} round{activeSession.excluded_interview_ids.length === 1 ? "" : "s"} excluded
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {sessionMine && (
                    <>
                      <button
                        onClick={toggleShare}
                        className="rounded-md p-1 text-ink-500 hover:bg-white hover:text-ink-900"
                        title={activeSession.shared_with_team ? "Stop sharing with team" : "Share with team"}
                        data-testid="chat-toggle-share"
                      >
                        <Share2 className="h-3.5 w-3.5" strokeWidth={STROKE} />
                      </button>
                      <button
                        onClick={archiveSession}
                        className="rounded-md p-1 text-ink-500 hover:bg-white hover:text-ink-900"
                        title="Archive this chat"
                        data-testid="chat-archive"
                      >
                        <Archive className="h-3.5 w-3.5" strokeWidth={STROKE} />
                      </button>
                    </>
                  )}
                  {!sessionMine && isAdmin && (
                    <span className="rounded-md bg-emerald-800/10 border border-emerald-800/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900" data-testid="chat-admin-view-badge">
                      Admin view · {activeSession.created_by_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Messages scroller */}
              <div
                ref={scrollerRef}
                className="flex-1 space-y-4 overflow-y-auto px-5 py-4"
                data-testid="chat-messages"
              >
                {(activeSession.messages || []).length === 0 && (
                  <div className="text-center text-[13px] text-ink-500" data-testid="chat-empty-thread">
                    Ask a question about what was said in these interviews.
                  </div>
                )}
                {(activeSession.messages || []).map((m) => (
                  <div
                    key={m.message_id}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-[14px] leading-relaxed ${
                        m.role === "user"
                          ? "bg-emerald-800 text-white"
                          : m.refusal_triggered
                          ? "border border-amber-200 bg-amber-50 text-amber-900"
                          : "border border-ink-200 bg-white text-ink-900"
                      }`}
                    >
                      {m.role === "assistant" ? (
                        <ChatAnswer content={m.content} />
                      ) : (
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      )}
                      {m.role === "assistant" && (
                        <div className="mt-1.5 flex items-center gap-2 text-[10px] text-ink-400">
                          {m.refusal_triggered && <CompliantBadge />}
                          <span>{fmtCost(m.est_cost_usd)}</span>
                          <span>·</span>
                          <span>{m.tokens_in} in / {m.tokens_out} out</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start" data-testid="chat-thinking">
                    <div className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-[13px] text-ink-500">
                      <Loader2 className="inline h-3 w-3 animate-spin mr-1.5" strokeWidth={STROKE} />
                      Searching transcripts…
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="border-t border-ink-200 bg-white px-5 py-3">
                {turnsRemaining <= 0 ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                    <span><AlertTriangle className="inline h-3 w-3 mr-1" strokeWidth={STROKE} /> 20-turn limit reached. Start a new chat to continue.</span>
                    <button
                      onClick={startNewSession}
                      className="rounded border border-amber-800 px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
                      data-testid="chat-start-new-after-limit"
                    >
                      New chat
                    </button>
                  </div>
                ) : !sessionMine ? (
                  <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] text-ink-600" data-testid="chat-readonly-notice">
                    You're viewing this chat read-only. Only {activeSession.created_by_name} can post here. {isAdmin && "(Admin view)"}
                  </div>
                ) : (
                  <div className="flex items-end gap-2">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                      placeholder="Ask what was said in these interviews…"
                      rows={2}
                      data-testid="chat-input"
                      className="flex-1 resize-none rounded-md border border-ink-200 bg-white px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-400 focus:border-emerald-800 focus:outline-none"
                      disabled={sending}
                    />
                    <button
                      onClick={send}
                      disabled={!draft.trim() || sending}
                      className="inline-flex items-center justify-center rounded-md bg-emerald-800 px-3 py-2 text-white hover:bg-emerald-900 disabled:opacity-50"
                      data-testid="chat-send"
                      aria-label="Send"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Send className="h-4 w-4" strokeWidth={STROKE} />}
                    </button>
                  </div>
                )}
                {quota && (
                  <div className="mt-2 flex items-center justify-end text-[10px] text-ink-400">
                    <span data-testid="chat-quota">
                      Workspace today: {fmtCost(quota.daily_spent_usd)} / {fmtCost(quota.daily_cap_usd)}
                      {quota.daily_remaining_usd < 1 && (
                        <span className="ml-1 text-amber-700"> · Low budget</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
